import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowDown } from '../../../assets/icons/arrow-down.svg';
import { ReactComponent as ArrowRight } from '../../../assets/icons/greater-than.svg';
import { ReactComponent as ArrowLeft } from '../../../assets/icons/lower-than.svg';
import { FONT_OBVIOUSLY, FONT_SIZE_16, FONT_WEIGHT_400 } from '../../constants/constants';
import { CLICK_ACTION, PAGINATOR_JUMP_TO_PAGE } from '../../constants/tagManager';
import TagManagerService from '../../services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../translations/commons';
import { GenericVoidFunction } from '../../types/genericTypes';
import StyledMenu, { IStyledMenuListProps } from '../StyledMenu/StyledMenu';
import './styles.scss';

const ACTUAL_PAGE_CLASS = 'actual-page';

interface ITablePaginationProps {
  rowsPerPageOptions: number[];
  count: number;
  rowsPerPage: number;
  actualPage: number;
  paginationIntervalText?: string;
  paginationPagesText?: string;
  marginTop?: number;
  sectionPage?: { section: string };
  onPageChange: GenericVoidFunction;
  onRowsPerPageChange: GenericVoidFunction;
}

const TablePagination = ({
  rowsPerPageOptions,
  count,
  rowsPerPage,
  actualPage,
  paginationIntervalText = TRANSLATIONS_GLOBAL.tablePagination.rows,
  paginationPagesText = TRANSLATIONS_GLOBAL.tablePagination.perPage,
  marginTop,
  sectionPage,
  onPageChange,
  onRowsPerPageChange
}: ITablePaginationProps) => {
  const [pages, setPages] = useState<number[]>([]);
  const [anchorEl, setAnchorEl] = useState(null);

  const { t } = useTranslation();

  const setPaginatorPages = useCallback(
    (dataLength: number, isInitial = false) => {
      const totalPages = Math.ceil(dataLength / rowsPerPage);
      const initialPages = [];
      for (let i = 0; i < totalPages; i++) {
        initialPages.push(i + 1);
      }
      setPages(initialPages);
      if (!isInitial) {
        onPageChange(0);
      }
    },
    [onPageChange, rowsPerPage]
  );

  useEffect(() => {
    setPaginatorPages(count, true);
  }, [count, setPaginatorPages, onPageChange]);

  const handlePaginatorClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const getFirstInPage = () => {
    return actualPage < 1 ? 1 : actualPage * rowsPerPage + 1;
  };

  const getLastInPage = () => {
    const aux = (actualPage + 1) * rowsPerPage;
    return count < aux ? count : aux;
  };

  const onClickNextPage = () => {
    const totalPages = Math.ceil(count / rowsPerPage);
    if (actualPage < totalPages - 1) {
      const nextPage = actualPage + 1;
      onPageChange(nextPage);
      TagManagerService.pushEvent(CLICK_ACTION, PAGINATOR_JUMP_TO_PAGE, {
        number: nextPage + 1,
        type: 'arrow',
        ...sectionPage
      });
    }
  };

  const onClickPreviousPage = () => {
    if (actualPage > 0) {
      const previousPage = actualPage - 1;
      onPageChange(previousPage);
      TagManagerService.pushEvent(CLICK_ACTION, PAGINATOR_JUMP_TO_PAGE, {
        number: previousPage + 1,
        type: 'arrow',
        ...sectionPage
      });
    }
  };

  const renderPagePaginator = (index: number, value: number) => {
    const totalPages = Math.ceil(count / rowsPerPage);
    const clickableIndexPage = (
      <button
        className={'clickable ' + getActualPageClass(index)}
        key={index}
        onClick={() => {
          onPageChange(index);
          TagManagerService.pushEvent(CLICK_ACTION, PAGINATOR_JUMP_TO_PAGE, {
            number: index + 1,
            type: 'number',
            ...sectionPage
          });
        }}
      >
        {value}
      </button>
    );
    if (showIndex(index, totalPages)) {
      return clickableIndexPage;
    }
    if (Math.abs(actualPage - index) === 2) {
      return <p key={index}>...</p>;
    }
  };

  const showIndex = (index: number, totalPages: number) =>
    index === 0 ||
    index === totalPages - 1 ||
    index === actualPage ||
    Math.abs(actualPage - index) === 1 ||
    (Math.abs(actualPage - (totalPages - 1)) === 3 && Math.abs(index - (totalPages - 1)) === 1) ||
    (Math.abs(actualPage - 3) === 0 && Math.abs(index - 1) == 0);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const styledMenuListProps: IStyledMenuListProps = {
    width: 75
  };

  const getActualPageClass = (index: number) => {
    return index === actualPage ? ACTUAL_PAGE_CLASS : '';
  };

  const changeRowsPerPage = (value: number) => {
    onRowsPerPageChange(value);
    setAnchorEl(null);
  };

  const paginatorMenu = () =>
    rowsPerPageOptions.map((value) => ({
      label: value.toString(),
      action: () => {
        changeRowsPerPage(value);
      }
    }));

  if (count <= 0) return <></>;

  return (
    <div className="paginator-row-container" style={{ marginTop }}>
      <div className="paginator-row first">
        <div className="total-collaborators">
          <p>{t(paginationIntervalText, { pageFirst: getFirstInPage(), pageLast: getLastInPage(), total: count })}</p>
        </div>
        <div className="collaborators-per-page">
          <button className="paginator-properties" onClick={handlePaginatorClick}>
            <div className="paginator-btn-content">{rowsPerPage}</div>
            <ArrowDown />
          </button>
          <StyledMenu
            anchorEl={anchorEl}
            handleClose={handleClose}
            styledMenuListProps={styledMenuListProps}
            menuItems={paginatorMenu()}
            font={{ family: FONT_OBVIOUSLY, size: FONT_SIZE_16, weight: FONT_WEIGHT_400 }}
          />
          <p>{t(paginationPagesText)}</p>
        </div>
      </div>
      <div className="paginator-row last">
        <div className="paginator">
          <button className="icon clickable" onClick={onClickPreviousPage}>
            <ArrowLeft data-testid="arrow-left" />
          </button>
          {pages.map((value, index) => renderPagePaginator(index, value))}
          <button className="icon clickable" onClick={onClickNextPage}>
            <ArrowRight data-testid="arrow-right" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TablePagination;
